import React, { useEffect } from "react";

export function Modal({ title, children, onClose, maxWidth = "max-w-5xl" }) {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();

    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-3">
      <div className="absolute inset-0 bg-black/55" onClick={onClose} />

      <div
        className={[
          "relative w-[95vw] rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl max-h-[92vh] overflow-hidden",
          maxWidth,
        ].join(" ")}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
            {title}
          </h3>

          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full h-9 w-9 inline-flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            ✕
          </button>
        </div>

        <div className="p-5 overflow-y-auto max-h-[calc(92vh-74px)]">
          {children}
        </div>
      </div>
    </div>
  );
}

export function Input({
  label,
  value,
  onChange,
  type = "text",
  required,
  className = "",
  placeholder = "",
  icon = null,
}) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
        {label}
      </div>

      <div className="relative">
        {icon ? (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {icon}
          </span>
        ) : null}

        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          placeholder={placeholder}
          className={[
            "w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400",
            icon ? "pl-9" : "",
          ].join(" ")}
        />
      </div>
    </label>
  );
}

export function Textarea({
  label,
  value,
  onChange,
  rows = 3,
  className = "",
  placeholder = "",
}) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
        {label}
      </div>

      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
      />
    </label>
  );
}

export function Select({ label, value, onChange, options, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
        {label}
      </div>

      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
      >
        {options.map((o) => (
          <option key={o.value ?? "null"} value={o.value ?? ""}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}